import { Injectable, Inject } from "@angular/core";
import { ClientNg } from "../api/ClientNg";
import { Onboarding, WebSiteUserRole, CompanyType, OnboardingStatus, Client } from "@salaxy/core";
import { ISingleItemRepository } from "../repositories/ISingleItemRepository";

/** Service that hanldes the interaction to server in the onboarding process. */
@Injectable()
export class OnboardingService implements ISingleItemRepository<Onboarding> {

  private _current: Onboarding;

  /** Creates a new instance of OnboardingService with dependency injection. */
  constructor(
    @Inject(ClientNg) private client: Client,
  ) { }

  /** Current onboarding object. */
  public get current(): Onboarding {
    return this._current;
  }

  /**
   * Gets a new blank object with default values, suitable for UI binding.
   * This is a synchronous method - does not go to the server and it is not 100% reliable in that way.
   * However, it shoud provide a basic object, good enough for most views.
   */
  public getBlank(accountType = WebSiteUserRole.None): Onboarding {
    return {
      accountType: WebSiteUserRole.Worker,
      person: {
        contact: {},
      },
      company: {
        businessId: null,
        companyType: CompanyType.Unknown,
        name: null,
        contact: {},
      },
      signature: {},
      ui: {},
      validation: {},
      status: OnboardingStatus.Open,
      products: {},
    };
  }

  /**
   * Creates a new item and sets it as current for editing and saving.
   * The item is not yet sent to server and thus it is not in the list, nor does it have an identifier.
   */
  public newCurrent(accountType = WebSiteUserRole.None) {
    this._current = this.getBlank(accountType);
  }

  /**
   * Saves changes to the current item.
   * @param callback - Optional callback that is called after save and susequent reloadList completes.
   */
  public saveCurrent(callback?: (data: Onboarding) => any): void {
    if (this.current) {
      this.save(this.current, callback);
    } else {
      // console.error("SaveCurrent called when current is not set.");
    }
  }

  /**
   * Adds or updates a given item.
   * Operation is determined based on id: null/''/'new' adds, other string values update.
   * @param item - Item that is updated.
   * @param callback - Optional callback that is called after save and susequent reloadList completes.
   */
  public save(item: Onboarding, callback?: (data: Onboarding) => any): void {
    this.client.onboardingsApi.saveOnboarding(item).then( (onboarding: Onboarding) => {
        this._current = onboarding;
        if (callback) {
          callback(this.current);
        }
    });
  }

  /**
   * Deletes the given item from repository if possible
   * @param id - Identifier of the item to delete
   * @param callback - Optional callback that is called after save and susequent reloadList completes.
   */
  public delete?(id: string, callback?: (data: Onboarding) => any) {
    this.client.onboardingsApi.deleteOnboarding(id).then ( () => {
      this._current = null;
      if (callback) {
        callback(this.current);
      }
    });
  }

  /** Loads the onboarding from the server and sets it as current. */
  public loadSingle(id: string, callback?: (data: Onboarding) => any): void {
    this.client.onboardingsApi.getOnboarding(id).then( (onboarding: Onboarding) => {
        this._current = onboarding;
        if (callback) {
          callback(this.current);
        }
    });
  }

  /** Send Sms Verification */
  public sendSmsVerificationPin(callback?: (data: Onboarding) => any): void {
    this.client.onboardingsApi.sendSmsVerificationPin(this.current).then( (onboarding: Onboarding) => {
        this._current = onboarding;
        if (callback) {
          callback(this.current);
        }
    });
  }

}
